import React from "react"
import Icon from "@kit/design/icon"
import { blocks } from "./blocks"
import { getFriendlySectionLabel } from "./section/get-friendly-section-label"

/**
 * This provides the `options` and `types` needed to render and filter the
 * buttons inside the block toolbar through convertkit-editor.
 *
 * @param {Array} blockTypes - an array of strings, each being a block type
 */

export function assembleBlockToolbarOptions(blockTypes) {
  // Remove falsy values from the array.
  const blocks = blockTypes.filter(Boolean)

  // Because landing pages use toolbar options with the same types as some of
  // these blocks, we need to append `_block` to make these types unique.
  const options = blocks.map(type => {
    const block = getBlockByType(type)

    return {
      type: `${type}_block`,
      label: block.label,
      icon: () => <Icon name={block.icon} />,
      menuIcon: block.menuIcon,
      operation: `insert_${type}_block`
    }
  })

  const types = options.map(block => block.type)

  return { options, types }
}

/**
 * Looks up a block by its type.
 *
 * @param {String} type - the type of a block; for instance, "gallery"
 */

export function getBlockByType(type) {
  return blocks.find(block => block.type === type)
}

/**
 * Looks up a block by its "_block"-appended type, which is used in the block
 * toolbar to differentiate between landing page collections and email blocks.
 *
 * @param {String} uniqueType - a block type appended with "_block"
 */

export function getBlockByToolbarType(uniqueType) {
  const type = uniqueType.replace("_block", "")
  return getBlockByType(type)
}

/**
 * Looks up the label for a block by its type.
 *
 * @param {String} type - the `type` of a block
 */

export function getLabelByType(type) {
  const config = blocks.find(block => block.type === type)
  return config?.label
}

/**
 * Returns a label for a block depending on business logic. For instance, we can
 * return the word "Footer" for the last section in an email.
 *
 * @param {Object} block
 * @param {Array} blocks
 */

export function getFriendlyLabel(block, blocks) {
  const label = getLabelByType(block.type)

  if (label === "Section") return getFriendlySectionLabel(block, blocks)

  return label
}

/**
 * Gets the DOM element of a block
 * @param {Object} block
 * @returns
 */
export function findDOMBlock(block) {
  const domElement = document.querySelector(
    `[data-block-id="${block.block_id}"]`
  )
  if (!domElement) {
    throw new Error(
      `dom element not found for block with block id ${block.block_id}`
    )
  }
  return domElement
}

/**
 * Outlook does not work with shorthand border declaration (border-width, border-style, etc)
 * Instead, longhand should be used (border-<position>: <style> <width> <color>;)

 * Also, seems like outlook does not handle border width 0.
 * Instead, we don't include the style when border width is 0.
 *
 * @param {Number|null} width - width of the border
 * @param {String} color - color of the border
 */
export const getBorderStyle = (width, color) => {
  if (!width) {
    return null
  } else {
    return `solid ${width}px ${color}`
  }
}

/**
 * To add your block to the block toolbar, add its type below. If it needs to
 * be flipped, use the `useFeature` hook and `&&` operator. For example:
 *
 *      const { enabled: galleriesEnabled } = useFeature("galleries")
 *
 *      const blocks = [
 *        galleriesEnabled && "gallery"
 *      ]
 */

export function useEnabledBlocks() {
  const blocks = [
    "video",
    "layout",
    "gallery",
    "icons",
    "links",
    "bandsintown",
    "productEmbed"
  ]

  return assembleBlockToolbarOptions(blocks)
}
